/* eslint-disable @typescript-eslint/no-explicit-any */
import { getAxiosApiClientByUser } from './axiosInstance'

// Define interfaces based on the schema
export interface UserResponse {
  userId: string
  firstName?: string
  lastName?: string
  email: string
}

export interface AgencyUserResponse {
  id: string
  userId: string
  agencyId: string
  roleName: string
  user?: UserResponse
}

export interface BrandRepositoryResponse {
  id: string
  brandName: string
  createdAt: string
  updatedAt?: string
  createdBy?: string
  agencyUserId?: string
  styleAssets?: Record<string, any>
  fontAssets?: Record<string, any>
  AgencyUser?: AgencyUserResponse
  // Add new access level fields from backend
  access_level?: string
  can_write?: boolean
  is_shared_access?: boolean
}

export interface BrandRepositoryCreateRequest {
  brandName: string
}

const showToast = (message: string, isSuccess: true | false) => {
  // Create a toast container
  const toast = document.createElement('div')
  toast.textContent = message
  toast.style.position = 'fixed'
  toast.style.bottom = '20px'
  toast.style.right = '20px'
  toast.style.padding = '10px 20px'
  toast.style.color = '#fff'
  toast.style.borderRadius = '5px'
  toast.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.3)'
  toast.style.zIndex = '9999'
  toast.style.backgroundColor = isSuccess ? '#4CAF50' : '#f44336' // Green for success, red for error
  toast.style.fontSize = '14px'

  // Append to body
  document.body.appendChild(toast)

  // Remove toast after 2 seconds
  setTimeout(() => {
    toast.remove()
  }, 3000)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const makeApiRequest = async (
  endpoint: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any,
  method: 'get' | 'post' | 'put' | 'delete',
  customHeaders?: Record<string, string>
) => {
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...customHeaders
    }
  }

  try {
    let response
    const api = await getAxiosApiClientByUser()

    switch (method) {
      case 'get':
        response = await api.get(endpoint, { ...config, params: payload }) // Queries go in params
        break
      case 'post':
        response = await api.post(endpoint, payload, config)
        break
      case 'put':
        response = await api.put(endpoint, payload, config)
        break
      case 'delete':
        response = await api.delete(endpoint, { ...config, data: payload }) // DELETE may need `data`
        break
      default:
        throw new Error(`Unsupported method: ${method}`)
    }

    return response.data
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(`Error fetching data: ${error.message}`)
    // throw new Error(  error)
    // showToast(error?.response?.data?.detail || error.message, false)
    throw error
  }
}

/**
 * Creates a new brand repository
 * @param brandName Name of the brand repository to create
 * @returns The created brand repository or null if there was an error
 */
export const createBrandRepository = async (
  brandName: string
): Promise<BrandRepositoryResponse | null> => {
  try {
    const payload: BrandRepositoryCreateRequest = { brandName }
    const response = await makeApiRequest(
      '/brand-repository/create',
      payload,
      'post'
    )

    return response
  } catch (error) {
    console.error('Error creating brand repository:', error)
    return null
  }
}

/**
 * Fetches all brand repositories for the current user
 * @returns Array of brand repositories or empty array if there was an error
 */
export const fetchBrandRepositories = async (): Promise<
  BrandRepositoryResponse[]
> => {
  try {
    const response = await makeApiRequest(
      '/brand-repository/repositories',
      {},
      'get'
    )

    return response || []
  } catch (error) {
    console.error('Error fetching brand repositories:', error)
    return []
  }
}

/**
 * Fetches a single brand repository by ID
 * @param repoId ID of the repository to fetch
 * @param isSharedAccess Whether this is accessed via shared link
 * @returns The brand repository or null if not found or error
 */
export const fetchBrandRepositoryById = async (
  repoId: string,
  isSharedAccess: boolean = false
): Promise<BrandRepositoryResponse | null> => {
  try {
    // Add query parameter for shared access if needed
    const params = isSharedAccess ? { is_shared_access: 'true' } : {}
    
    const response = await makeApiRequest(
      `/brand-repository/repositories/${repoId}`,
      params,
      'get'
    )

    return response
  } catch (error) {
    console.error(`Error fetching brand repository with ID ${repoId}:`, error)
    return null
  }
}

/**
 * Deletes a brand repository by ID
 * @param repoId ID of the repository to delete
 * @returns The deleted repository if successful, null otherwise
 */
export const deleteBrandRepository = async (
  repoId: string
): Promise<BrandRepositoryResponse | null> => {
  try {
    const response = await makeApiRequest(
      `/brand-repository/delete/${repoId}`,
      {},
      'delete'
    )

    // if (response) {
    //   showToast('Brand kit deleted successfully', true)
    // }

    return response
  } catch (error) {
    console.error(`Error deleting brand repository with ID ${repoId}:`, error)
    return null
  }
}

/**
 * Updates a brand repository's style assets
 * @param repoId ID of the repository to update
 * @param styleAssets Style assets to update
 * @param logo Logo to update
 * @param icon Icon to add
 * @returns The updated repository if successful, null otherwise
 */
export const updateBrandRepository = async (
  repoId: string,
  styleAssets: Record<string, any>,
  logo?: File,
  icon?: File | null,
  fontAssets?: Record<string, any>
): Promise<BrandRepositoryResponse | null> => {
  try {
    // For updating just the style assets through the JSON payload
    const formData = new FormData()

    formData.append('style_assets', JSON.stringify(styleAssets))
    if (fontAssets) {
      formData.append('font_assets', JSON.stringify(fontAssets))
    }

    if (logo) {
      formData.append('logo', logo)
    }

    if (icon) {
      formData.append('social_icon', icon)
    }

    // Custom headers for form data
    const headers = {
      'Content-Type': 'multipart/form-data'
    }

    const response = await makeApiRequest(
      `/brand-repository/update/${repoId}`,
      formData,
      'put',
      headers
    )

    // if (response) {
    //   showToast('Brand kit updated successfully', true)
    // }

    return response
  } catch (error) {
    console.error(`Error updating brand repository with ID ${repoId}:`, error)
    throw error
  }
}

/**
 * Updates a brand repository's style assets
 * @param repoId ID of the repository to update
 * @param styleAssets Style assets to update
 * @returns The updated repository if successful, null otherwise
 */
export const updateBrandRepositoryStyleAssets = async (
  repoId: string,
  styleAssets: Record<string, any>
): Promise<BrandRepositoryResponse | null> => {
  try {
    // For updating just the style assets through the JSON payload
    const formData = new FormData()
    formData.append('style_assets', JSON.stringify(styleAssets))

    // Custom headers for form data
    const headers = {
      'Content-Type': 'multipart/form-data'
    }

    const response = await makeApiRequest(
      `/brand-repository/update/${repoId}`,
      formData,
      'put',
      headers
    )

    if (response) {
      showToast('Brand kit updated successfully', true)
    }

    return response
  } catch (error) {
    console.error(`Error updating brand repository with ID ${repoId}:`, error)
    return null
  }
}

/**
 * Updates a brand repository's name
 * @param repoId ID of the repository to update
 * @param brandName New name for the repository
 * @returns The updated repository if successful, null otherwise
 */
export const updateBrandRepositoryName = async (
  repoId: string,
  brandName: string
): Promise<BrandRepositoryResponse | null> => {
  try {
    // The update API expects FormData
    const formData = new FormData()

    formData.append('brand_name', brandName)

    // Since we're sending FormData, we need appropriate headers
    const headers = {
      'Content-Type': 'multipart/form-data'
    }

    const response = await makeApiRequest(
      `/brand-repository/update/${repoId}`,
      formData,
      'put',
      headers
    )

    // if (response) {
    //   showToast('Brand kit renamed successfully', true)
    // }

    return response
  } catch (error) {
    console.error(
      `Error updating brand repository name with ID ${repoId}:`,
      error
    )
    return null
  }
}

export const shareBrandRepository = async (
  repoId: string
): Promise<{ share_url: string } | null> => {
  try {
    const response = await makeApiRequest(
      `/brand-repository/share/${repoId}`,
      {},
      'post'
    )

    // if (response) {
    //   showToast('Share link generated successfully', true)
    // }

    return response
  } catch (error) {
    console.error(`Error sharing brand repository with ID ${repoId}:`, error)
    // showToast('Failed to generate share link', false)
    return null
  }
}

/**
 * Access a shared brand repository using a token
 * @param token The sharing token for the repository
 * @returns The accessed brand repository or null if there was an error
 */
export const accessSharedBrandRepository = async (
  token: string
): Promise<BrandRepositoryResponse | null> => {
  try {
    const response = await makeApiRequest(
      `/brand-repository/shared/${token}`,
      {},
      'get'
    )

    return response
  } catch (error) {
    console.error('Error accessing shared repository:', error)
    return null
  }
}
